import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSiteData } from '../context/SiteDataContext';
import { useLanguage } from '../context/LanguageContext';

const Services = () => {
  const { siteData } = useSiteData();
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const [selectedService, setSelectedService] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('services');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const serviceIcons = {
    '🏠': (
      <svg className="w-8 md:w-12 h-8 md:h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
    '🏢': (
      <svg className="w-8 md:w-12 h-8 md:h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    ),
    '⚡': (
      <svg className="w-8 md:w-12 h-8 md:h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    '🏗️': (
      <svg className="w-8 md:w-12 h-8 md:h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
      </svg>
    )
  };

  return (
    <section id="services" className="py-16 md:py-20 bg-gray-50 font-sans pt-header">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className={`text-center mb-12 md:mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {t('services.title')}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-orange-500 to-blue-600 mx-auto mb-6 md:mb-8"></div>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {t('services.subtitle')}
          </p>
        </div>

        {/* Services Grid - Improved mobile layout */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8 mb-12 md:mb-16">
          {siteData.services.map((service: any, index: number) => (
            <div
              key={service.id}
              className={`group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden cursor-pointer transform hover:-translate-y-2 ${
                selectedService === index ? 'ring-4 ring-orange-500 shadow-2xl scale-105' : ''
              } ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${index * 150}ms` }}
              onClick={() => setSelectedService(index)}
            >
              {/* Icon Header */}
              <div className={`p-6 md:p-8 text-center transition-all duration-300 ${
                selectedService === index ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white' : 'bg-gray-100 text-orange-600'
              }`}>
                <div className="inline-flex items-center justify-center w-12 h-12 md:w-16 md:h-16 rounded-full bg-white/20 mb-3 md:mb-4">
                  {serviceIcons[service.icon as keyof typeof serviceIcons] || <span className="text-2xl md:text-3xl">{service.icon}</span>}
                </div>
                <h3 className="text-lg md:text-xl font-bold leading-tight">{service.title[language]}</h3>
              </div>

              {/* Content */}
              <div className="p-4 md:p-6">
                <p className="text-gray-600 mb-4 md:mb-6 leading-relaxed text-sm md:text-base">
                  {service.description[language]}
                </p>

                {/* Features */}
                <div className="space-y-2 md:space-y-3 mb-4 md:mb-6">
                  {service.features[language].slice(0, 3).map((feature: string, featureIndex: number) => (
                    <div key={featureIndex} className="flex items-start text-xs md:text-sm">
                      <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-orange-500 rounded-full mr-2 md:mr-3 flex-shrink-0 mt-1.5 md:mt-2"></div>
                      <span className="text-gray-700 leading-relaxed">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* CTA Buttons - Improved mobile layout */}
                <div className="flex flex-col space-y-2 md:space-y-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/hizmetler/${service.id}`);
                    }}
                    className="w-full bg-orange-500 text-white py-2.5 md:py-3 px-4 md:px-6 rounded-xl font-semibold hover:bg-orange-600 transition-all duration-300 shadow-lg hover:shadow-xl border-2 border-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 text-sm md:text-base min-h-[44px]"
                    aria-label={`${service.title[language]} hizmeti hakkında detaylı bilgi al`}
                  >
                    {t('services.detail')}
                  </button>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      const phoneNumber = '905372959612';
                      const message = encodeURIComponent(t('whatsapp.service').replace('{service}', service.title[language]));
                      const whatsappUrl = `https://wa.me/${phoneNumber}?text=${message}`;
                      window.open(whatsappUrl, '_blank');
                    }}
                    className="w-full bg-green-500 text-white py-2.5 md:py-3 px-4 md:px-6 rounded-xl font-semibold hover:bg-green-600 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 text-sm md:text-base min-h-[44px]"
                    aria-label={`${service.title[language]} hizmeti için WhatsApp ile iletişim`}
                  >
                    <svg className="w-4 h-4 md:w-5 md:h-5 mr-2" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                    </svg>
                    WhatsApp
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Selected Service Details - Improved responsive design */}
        {selectedService !== null && siteData.services[selectedService] && (
          <div className={`bg-white rounded-2xl shadow-2xl p-6 md:p-8 lg:p-12 transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
              <div className="order-2 lg:order-1">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 md:mb-6">
                  {siteData.services[selectedService]?.title[language]}
                </h3>
                <p className="text-base md:text-lg text-gray-600 mb-6 md:mb-8 leading-relaxed">
                  {siteData.services[selectedService]?.description[language]}
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 mb-6 md:mb-8">
                  {siteData.services[selectedService]?.features[language].map((feature: string, index: number) => (
                    <div key={index} className="flex items-center">
                      <div className="w-5 h-5 md:w-6 md:h-6 bg-green-500 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                        <svg className="w-3 h-3 md:w-4 md:h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="font-medium text-gray-700 text-sm md:text-base">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
                  <button 
                    onClick={() => {
                      const phoneNumber = '905372959612';
                      const message = encodeURIComponent(t('whatsapp.service').replace('{service}', siteData.services[selectedService]?.title[language]));
                      const whatsappUrl = `https://wa.me/${phoneNumber}?text=${message}`;
                      window.open(whatsappUrl, '_blank');
                    }}
                    className="btn-primary flex-1 sm:flex-none min-h-[48px] text-sm md:text-base"
                  >
                    {t('services.quote')}
                  </button>
                  <button 
                    onClick={() => navigate(`/hizmetler/${siteData.services[selectedService]?.id}`)}
                    className="btn-secondary flex-1 sm:flex-none min-h-[48px] text-sm md:text-base"
                  >
                    {t('services.more')}
                  </button>
                </div>
              </div>

              <div className="relative order-1 lg:order-2">
                <div className="bg-gradient-to-br from-orange-500 to-blue-600 rounded-2xl p-6 md:p-8 text-white text-center">
                  <div className="text-4xl md:text-6xl mb-4">
                    {serviceIcons[siteData.services[selectedService]?.icon as keyof typeof serviceIcons] || siteData.services[selectedService]?.icon}
                  </div>
                  <h4 className="text-xl md:text-2xl font-bold mb-3 md:mb-4">{t('services.emergency.title')}</h4>
                  <p className="text-orange-100 mb-4 md:mb-6 text-sm md:text-base leading-relaxed">
                    {t('services.emergency.subtitle')}
                  </p>
                  <div className="flex flex-col space-y-3">
                    <a
                      href="tel:+905372959612"
                      className="bg-white text-orange-600 px-6 md:px-8 py-3 rounded-xl font-bold hover:bg-gray-100 transition-colors text-center text-sm md:text-base min-h-[48px] flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-white"
                    >
                      <span className="text-lg mr-2">📞</span>
                      {t('nav.call')}
                    </a>
                    <button 
                      onClick={() => {
                        const phoneNumber = '905372959612';
                        const message = encodeURIComponent(t('whatsapp.emergency'));
                        const whatsappUrl = `https://wa.me/${phoneNumber}?text=${message}`;
                        window.open(whatsappUrl, '_blank');
                      }}
                      className="bg-green-500 text-white px-6 md:px-8 py-3 rounded-xl font-bold hover:bg-green-600 transition-colors flex items-center justify-center text-sm md:text-base min-h-[48px] focus:outline-none focus:ring-2 focus:ring-green-300"
                    >
                      <svg className="w-4 h-4 md:w-5 md:h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                      </svg>
                      WhatsApp
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Services;
